import { rgb } from "pdf-lib";

import { getTranslation } from "../translate";
import { generateQR } from "./pdfUtils";
import { getPosterQrTargetUrl } from "./beefProductVideoUrl";
import {
  CARD_CONFIG,
  HEADER_CONFIG,
  PORTION_COLORS,
  QR_CONFIG,
} from "./pdfConstants";


/**
 * Get color based on beef cut portion
 */
const getColorForPortion = (beefCut) => {
  // Try hierarchy levels in order until we find a matching color
  const namesToTry = [
    beefCut?.parent_group?.parent_group?.translations?.[0]?.name,
    beefCut?.parent_group?.translations?.[0]?.name,
    beefCut?.translations?.[0]?.name,
  ];

  for (const portionName of namesToTry) {
    if (!portionName) continue;

    // Convert to uppercase and check for matching portion in PORTION_COLORS
    const upperName = portionName.toUpperCase();

    // Check if the name contains any of the portion keywords
    for (const [portion, color] of Object.entries(PORTION_COLORS)) {
      if (upperName.includes(portion)) {
        return color;
      }
    }
  }

  // Default to first background color if no match found
  return PORTION_COLORS["CHUCK"];
};


/**
 * White card with light border — no product photo (CB-3283).
 */
const drawSimpleCard = (page, cardX, cardY, cardWidth, cardHeight, colorGradient) => {

  page.drawRectangle({
    x: cardX - 1,
    y: cardY - 1,
    width: cardWidth + 2,
    height: cardHeight + 2,
    color: rgb(1, 1, 1),
  });
  page.drawRectangle({
    x: cardX,
    y: cardY,
    width: cardWidth,
    height: cardHeight,
    color: colorGradient,
  });
};

const buildTitleLines = (beefCut, languageCode) => {
  const translated = getTranslation(
    beefCut,
    { key: "languages_id", code: languageCode },
    "translations",
  );
  const englishName =
    translated?.name || beefCut.translations?.[0]?.name || "";
  const maxChars = HEADER_CONFIG.MAX_CHARS_PER_LINE;
  const maxLines = HEADER_CONFIG.MAX_LINES;

  const lines = [];
  let remaining = englishName.toUpperCase();
  for (let i = 0; i < maxLines && remaining.length > 0; i++) {
    lines.push(remaining.substring(0, maxChars));
    remaining = remaining.substring(maxChars);
  }
  return lines;
};

/**
 * Product name only (uppercase), bottom of card — horizontally centered.
 */
const drawCardTitle = (
  page,
  cardY,
  lines,
  headerFont,
  cardX,
  cardWidth,
) => {
  const bottom = HEADER_CONFIG.BOTTOM_LINE_BASELINE_FROM_BOTTOM;
  const textSize = HEADER_CONFIG.TEXT_SIZE;
  lines.forEach((line, lineIndex) => {
    const baselineY =
      cardY +
      bottom +
      (lines.length - 1 - lineIndex) * HEADER_CONFIG.LINE_HEIGHT;

    const lineWidth = headerFont.widthOfTextAtSize(line, textSize);
    const x = cardX + (cardWidth - lineWidth) / 2;

    page.drawText(line, {
      x,
      y: baselineY,
      size: textSize,
      font: headerFont,
      color: HEADER_CONFIG.TEXT_COLOR,
    });
  });
};

/**
 * Centered QR above the title; size prefers QR_CONFIG.SIZE but shrinks if needed for two-line names.
 */
const drawCardQr = async (
  page,
  pdfDoc,
  cardY,
  beefCut,
  cardX,
  cardWidth,
  cardHeight,
  languageCode,
  titleLines,
  retailer,
) => {
  const targetUrl = getPosterQrTargetUrl(beefCut, languageCode, retailer);
  if (!targetUrl) {
    return;
  }

  const qrCodeBytes = await generateQR(targetUrl);
  if (!qrCodeBytes) return;

  const qrImage = await pdfDoc.embedPng(qrCodeBytes);

  /** Treat empty name as one line of vertical space so QR layout math stays valid. */
  const titleLineCount = Math.max(1, titleLines.length);
  const firstLineBaseline =
    cardY +
    HEADER_CONFIG.BOTTOM_LINE_BASELINE_FROM_BOTTOM +
    (titleLineCount - 1) * HEADER_CONFIG.LINE_HEIGHT;
  const titleVisualTop = firstLineBaseline + HEADER_CONFIG.TEXT_SIZE;
  const minQrBottom = titleVisualTop + HEADER_CONFIG.GAP_QR_TO_TITLE;
  const maxQrTop =
    cardY + cardHeight - QR_CONFIG.TOP_OFFSET_FROM_CARD_TOP;

  const available = maxQrTop - minQrBottom;
  const size = Math.min(QR_CONFIG.SIZE, Math.max(0, available));
  if (size < 1) {
    return;
  }

  const qrY = maxQrTop - size;
  const qrX = cardX + (cardWidth - size) / 2;

  page.drawImage(qrImage, {
    x: qrX,
    y: qrY,
    width: size,
    height: size,
  });
};

/**
 * Render a single beef product card: localized name + QR only (CB-3283).
 */
export const renderBeefCutCard = async (
  page,
  pdfDoc,
  beefCut,
  fonts,
  cardX = CARD_CONFIG.START_X,
  cardY = CARD_CONFIG.START_Y,
  cardWidth = CARD_CONFIG.CARD_WIDTH,
  languageCode = "en-US",
  retailer,
) => {
  const cardHeight = CARD_CONFIG.CARD_HEIGHT;
  const colorGradient = getColorForPortion(beefCut);

  const titleLines = buildTitleLines(beefCut, languageCode);

  drawSimpleCard(page, cardX, cardY, cardWidth, cardHeight, colorGradient);
  await drawCardQr(
    page,
    pdfDoc,
    cardY,
    beefCut,
    cardX,
    cardWidth,
    cardHeight,
    languageCode,
    titleLines,
    retailer,
  );
  drawCardTitle(page, cardY, titleLines, fonts.bold, cardX, cardWidth);
};
